import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Logo from './Logo';
import { useTranslation } from 'react-i18next';
import { Bell, MessageSquare, Activity, BookOpen } from 'lucide-react';
import { getUnreadCount } from '@/utils/unread';

const dash = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/dop.png";
const comp = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/company.png";
const prof = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/profile.png";
const DataRoom = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/data.png";
const fin = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/finicial.png";
const kpi = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/kpi.png";
const set = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/setting.png";
const ser = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/service.png";
const ir = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/ir score.png";
const rf = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/refer.png";

type NavItem = {
  label: string;
  path?: string;
  iconPath?: string;
  lucideIcon?: React.ElementType;
};

type SidebarProps = {
  onLogout?: () => void;
};

// Communications items are identical for both service views.
const navComms: NavItem[] = [
  { label: "sidebar.notifications", path: "/notifications", lucideIcon: Bell },
  { label: "sidebar.messageAdmin", path: "/message-admin", lucideIcon: MessageSquare },
];

// Determine which service view drives the sidebar. The Dashboard persists the
// user's active view to localStorage ('activeServiceView'); fall back to the
// signup/setup service when not set.
function getServiceMode(): 'accounting' | 'consulting' {
  const explicit = localStorage.getItem('activeServiceView');
  if (explicit === 'accounting' || explicit === 'consulting') return explicit;
  const ss = localStorage.getItem('setupService');
  return (ss === 'operate' || ss === 'incorporate') ? 'accounting' : 'consulting';
}

export default function Sidebar({ onLogout }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  // Keep the sidebar in sync with the Dashboard's active service view.
  const [mode, setMode] = useState<'accounting' | 'consulting'>(getServiceMode);
  useEffect(() => {
    const update = () => setMode(getServiceMode());
    window.addEventListener('storage', update);
    window.addEventListener('serviceViewChanged', update);
    return () => {
      window.removeEventListener('storage', update);
      window.removeEventListener('serviceViewChanged', update);
    };
  }, []);
  const isAccounting = mode === 'accounting';

  // Unread notification count for the Notifications nav badge.
  const [unread, setUnread] = useState(getUnreadCount());
  useEffect(() => {
    const upd = () => setUnread(getUnreadCount());
    window.addEventListener('connct-unread', upd);
    window.addEventListener('storage', upd);
    return () => { window.removeEventListener('connct-unread', upd); window.removeEventListener('storage', upd); };
  }, []);

  const navPrimary: NavItem[] = [
    { label: "sidebar.dashboard", path: "/dashboard", iconPath: dash },
    { label: "sidebar.company", path: "/company-profile", iconPath: comp },
    { label: "sidebar.profile", path: "/profile", iconPath: prof },
    { label: "sidebar.dataRoom", path: isAccounting ? "/accounting/data-room" : "/data-room", iconPath: DataRoom },
    { label: "sidebar.articles", path: "/articles", lucideIcon: BookOpen },
  ];

  const navAnalytics: NavItem[] = isAccounting
    ? [
        { label: "sidebar.financialHealth", path: "/financial-health-check", lucideIcon: Activity },
      ]
    : [
        { label: "sidebar.irScore", path: "/ir-score", iconPath: ir },
        { label: "sidebar.financials", path: "/financials", iconPath: fin },
        { label: "sidebar.kpis", path: "/kpis", iconPath: kpi },
      ];

  const navServices: NavItem[] = isAccounting
    ? [
        { label: "My Services", path: "/my-services", iconPath: ser },
        { label: "sidebar.referAndEarn", path: "/refer-and-earn", iconPath: rf },
        { label: "sidebar.settings", path: "/settings", iconPath: set },
      ]
    : [
        { label: "sidebar.referAndEarn", path: "/refer-and-earn", iconPath: rf },
        { label: "sidebar.settings", path: "/settings", iconPath: set },
      ];

  const handleNavigation = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const defaultLogout = () => {
    logout();
    navigate('/');
  };

  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    } else {
      defaultLogout();
    }
  };



  return (
    <aside className="hidden md:flex w-64 bg-white border-e border-gray-200 flex-col fixed h-full start-0">
      <div className="p-4">
        <div className="bg-gray-50 rounded-xl p-6 flex justify-center items-center">
          {/* explicit link to dashboard to ensure logo always navigates to /dashboard */}
          <a href="/dashboard" className="inline-block">
            <Logo className="h-8" to="/dashboard" />
          </a>
        </div>
      </div>


      <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
        {/* Primary Navigation */}
        <div>
          <ul className="space-y-1">
            {navPrimary.map((item) => (
              /* data-tour lets the onboarding tour spotlight a nav entry by path. */
              <li key={item.label} data-tour={`nav:${item.path}`}>
                <button
                  onClick={() => handleNavigation(item.path)}
                  disabled={!item.path}
                  className={`w-full text-start px-4 py-2 rounded-lg text-sm ${isActive(item.path)
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : item.path
                      ? "text-gray-700 hover:bg-gray-50 cursor-pointer"
                      : "text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <div className="flex items-center">
                    {item.iconPath ? (
                      <img src={item.iconPath} alt={`${t(item.label)} icon`} className="w-5 h-5 me-3 object-contain" loading="lazy" />
                    ) : item.lucideIcon ? (
                      <item.lucideIcon className="w-5 h-5 me-3 shrink-0" />
                    ) : (
                      <span className="w-5 h-5 me-3 inline-block" />
                    )}
                    <span>{t(item.label)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Analytics Section */}
        <div>
          <h3 className="px-4 text-xs font-bold text-black uppercase mb-2">
            Analytics
          </h3>
          <ul className="space-y-1">
            {navAnalytics.map((item) => (
              <li key={item.label} data-tour={`nav:${item.path}`}>
                <button
                  onClick={() => handleNavigation(item.path)}
                  disabled={!item.path}
                  className={`w-full text-start px-4 py-2 rounded-lg text-sm ${isActive(item.path)
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : item.path
                      ? "text-gray-700 hover:bg-gray-50 cursor-pointer"
                      : "text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <div className="flex items-center">
                    {item.iconPath ? (
                      <img src={item.iconPath} alt={`${t(item.label)} icon`} className="w-5 h-5 me-3 object-contain" loading="lazy" />
                    ) : item.lucideIcon ? (
                      <item.lucideIcon className="w-5 h-5 me-3 shrink-0" />
                    ) : (
                      <span className="w-5 h-5 me-3 inline-block" />
                    )}
                    <span>{t(item.label)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Communications Section */}
        <div>
          <h3 className="px-4 text-xs font-bold text-black uppercase mb-2">
            Communications
          </h3>
          <ul className="space-y-1">
            {navComms.map((item) => (
              <li key={item.label} data-tour={`nav:${item.path}`}>
                <button
                  onClick={() => handleNavigation(item.path)}
                  disabled={!item.path}
                  className={`w-full text-start px-4 py-2 rounded-lg text-sm ${isActive(item.path)
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : item.path
                      ? "text-gray-700 hover:bg-gray-50 cursor-pointer"
                      : "text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <div className="flex items-center">
                    {item.lucideIcon ? (
                      <item.lucideIcon className="w-5 h-5 me-3 shrink-0" />
                    ) : (
                      <span className="w-5 h-5 me-3 inline-block" />
                    )}
                    <span>{t(item.label)}</span>
                    {item.label === 'sidebar.notifications' && unread > 0 && (
                      <span className="ms-auto min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-black leading-none">{unread > 9 ? '9+' : unread}</span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Services Section */}
        <div>
          <ul className="space-y-1">
            {navServices.map((item) => (
              <li key={item.label} data-tour={`nav:${item.path}`}>
                <button
                  onClick={() => handleNavigation(item.path)}
                  disabled={!item.path}
                  className={`w-full text-start px-4 py-2 rounded-lg text-sm ${isActive(item.path)
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : item.path
                      ? "text-gray-700 hover:bg-gray-50 cursor-pointer"
                      : "text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <div className="flex items-center">
                    {item.iconPath ? (
                      <img src={item.iconPath} alt={`${t(item.label)} icon`} className="w-5 h-5 me-3 object-contain" loading="lazy" />
                    ) : item.lucideIcon ? (
                      <item.lucideIcon className="w-5 h-5 me-3 shrink-0" />
                    ) : (
                      <span className="w-5 h-5 me-3 inline-block" />
                    )}
                    <span>{t(item.label)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <div className="p-4 border-t border-gray-200">
        <button
          onClick={handleLogoutClick}
          className="w-full text-start px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-log-out-icon lucide-log-out"><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /></svg>
          <span>{t('sidebar.signOut')}</span>
        </button>
      </div>
    </aside>
  );
}
